import React from "react";
import Icon from '@mdi/react';
import { mdiInformationSlabCircle } from '@mdi/js';
import { Checkbox, Radio } from "antd";
import { BreakdownRow } from "./SummaryRows";
import { PaymentAmounts } from "./usePaymentAmounts";
import { FareType, PayMode } from "./types";

const tabStyle = (active: boolean): React.CSSProperties => ({
  flex: 1,
  padding: "12px 16px",
  background: "none",
  border: "none",
  borderBottom: active ? "2px solid #DC1A22" : "2px solid transparent",
  color: active ? "#DC1A22" : "#8c8c8c",
  fontWeight: active ? 700 : 400,
  fontSize: 14,
  cursor: "pointer",
  fontFamily: "Poppins, sans-serif",
  letterSpacing: "0.05em",
});

// How much of the booking the customer is paying: the deposit, the whole
// remaining balance, or just the add-ons they pick.
export default function PaymentTermsPanel(
  props: {
    paymentInfo: any;
    amounts: PaymentAmounts;
    fareType: FareType;
    onFareTypeChange: (fareType: FareType) => void;
    payMode: PayMode;
    onPayModeChange: (payMode: PayMode) => void;
    selectedAddonIds: number[];
    onToggleAddon: (id: number, checked: boolean) => void;
    onToggleAllAddons: (checked: boolean) => void;
    onViewBreakdown: () => void;
  }
) {
  const {
    paymentInfo, amounts, fareType, onFareTypeChange, payMode, onPayModeChange,
    selectedAddonIds, onToggleAddon, onToggleAllAddons, onViewBreakdown,
  } = props;

  const {
    money, addons, hasAddons, selectableAddons, selectableTotal, currSelectableTotal,
    tourPrice, currTourPrice, paidToDate, currPaidToDate, balanceAmount, currBalanceAmount, deposit, currDeposit,
    depositPerPax, currDepositPerPax, remainingAfterDeposit, currRemainingAfterDeposit,
    showDepositToggle, payingDeposit, nothingSelected,
  } = amounts;

  const displayTourPrice = money(tourPrice, currTourPrice);
  const paidToDateRow = paidToDate > 0 && (
    <BreakdownRow label="Paid to date" value={`- ${money(paidToDate, currPaidToDate)}`} />
  );
  const displayRemaining = money(remainingAfterDeposit, currRemainingAfterDeposit);
  // `deposit` is only what is still owed of it, so a per-pax split is only
  // meaningful before anything has been paid.
  const depositPerPaxText = paidToDate > 0
    ? ""
    : ` (${money(depositPerPax, currDepositPerPax)} × ${paymentInfo.total_quantity} pax)`;

  return (
    <div className="b2c2b-section_panel payment-terms-panel" style={{ marginBottom: 24, overflow: "hidden" }}>
      <div className="b2c2b-section_panel-header">
        <label>Payment terms</label>
      </div>

      {showDepositToggle && (
        <div style={{ display: "flex", borderBottom: "1px solid #ECECEC" }}>
          <button type="button" style={tabStyle(fareType === "customer_deposit")} onClick={() => onFareTypeChange("customer_deposit")}>
            Pay deposit
          </button>
          <button type="button" style={tabStyle(fareType === "customer_full_fare")} onClick={() => onFareTypeChange("customer_full_fare")}>
            Pay in full
          </button>
        </div>
      )}

      {payingDeposit ? (
        <>
          <BreakdownRow label="Tour price" value={displayTourPrice} />
          {paidToDateRow}
          <BreakdownRow
            label="Deposit amount"
            value={`${money(deposit, currDeposit)}${depositPerPaxText}`}
          />
          <BreakdownRow label="Remaining balance" value={displayRemaining} valueStyle={{ color: "#DC1A22", fontWeight: 600 }} />
          {paymentInfo.payment_deadline && (
            <div style={{ margin: "12px 20px 20px", background: "#FFFBE6", border: "1px solid #FFE58F", borderRadius: 8, padding: "10px 16px", fontSize: 14 }}>
              Final payment of <strong>{displayRemaining}</strong> is due before <strong>{paymentInfo.payment_deadline}</strong>
            </div>
          )}
        </>
      ) : hasAddons ? (
        <div style={{ padding: "16px 20px 20px" }}>
          <p style={{ marginBottom: 16, fontSize: 14 }}>Choose how you'd like to pay.</p>

          <Radio.Group value={payMode} onChange={(e) => onPayModeChange(e.target.value)} style={{ display: "block" }}>
            <div
              className={`pay-mode-card ${payMode === "remaining_balance" ? "selected" : ""}`}
              onClick={() => onPayModeChange("remaining_balance")}
            >
              <Radio value="remaining_balance" />
              <div>
                <div style={{ fontSize: 14 }}>Pay Remaining Balance (Includes all unpaid add-ons)</div>
                <div style={{ color: "#DC1A22", fontSize: 16, fontWeight: 700, margin: "6px 0" }}>
                  {money(balanceAmount, currBalanceAmount)}
                </div>
                <button
                  type="button"
                  className="pay-mode-card_link"
                  onClick={(e) => { e.stopPropagation(); onViewBreakdown(); }}
                >
                  View price breakdown
                </button>
              </div>
            </div>

            <div
              className={`pay-mode-card ${payMode === "selected_addons" ? "selected" : ""}`}
              onClick={() => onPayModeChange("selected_addons")}
            >
              <Radio value="selected_addons" />
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>Pay selected add-ons only</div>
                <div style={{ fontSize: 14, color: "#595959", marginTop: 2 }}>Choose one or more add-ons below.</div>
              </div>
            </div>
          </Radio.Group>

          <div className="addon-list">
            <div className="addon-list_row">
              <Checkbox
                checked={selectableAddons.length > 0 && selectedAddonIds.length === selectableAddons.length}
                indeterminate={selectedAddonIds.length > 0 && selectedAddonIds.length < selectableAddons.length}
                disabled={payMode !== "selected_addons" || selectableAddons.length === 0}
                onChange={(e) => onToggleAllAddons(e.target.checked)}
              >
                All add-ons
              </Checkbox>
              <span className="addon-list_amount">{money(selectableTotal, currSelectableTotal)}</span>
            </div>

            {addons.map((addon) => {
              const inProgress = addon.payment_status === "in_progress";
              return (
                <div key={addon.id} className="addon-list_row addon-list_row-child">
                  <Checkbox
                    checked={selectedAddonIds.includes(addon.id)}
                    disabled={payMode !== "selected_addons" || inProgress}
                    onChange={(e) => onToggleAddon(addon.id, e.target.checked)}
                  >
                    {addon.remark}
                    {inProgress && <span className="addon-list_tag">Payment in progress</span>}
                  </Checkbox>
                  <span className="addon-list_amount">{money(addon.amount, addon.currency_amount)}</span>
                </div>
              );
            })}
          </div>

          {nothingSelected && (
            <div className="addon-hint">
              <Icon path={mdiInformationSlabCircle} size="24px" />
              <span>Please select at least one add-on to continue.</span>
            </div>
          )}
        </div>
      ) : (
        <>
          <BreakdownRow label="Tour price" value={displayTourPrice} />
          {paidToDateRow}
          <BreakdownRow
            label="Remaining balance after payment"
            value={`${money(0, 0)} — fully paid`}
            valueStyle={{ color: "#52C41A", fontWeight: 600 }}
          />
        </>
      )}
    </div>
  );
}
